// Reminder types
export type ReminderType = 'one_time' | 'recurring';

// Reminder status
export type ReminderStatus = 'pending' | 'triggered' | 'snoozed' | 'dismissed' | 'completed';

// Recurrence frequency
export type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly';

// Recurrence config
export interface ReminderRecurrence {
  frequency: RecurrenceFrequency;
  interval: number;
  endDate?: string;
  daysOfWeek?: number[];
  dayOfMonth?: number;
}

// Linked entities (populated)
export interface ReminderTask {
  _id: string;
  title: string;
  status: string;
}

export interface ReminderProject {
  _id: string;
  name: string;
  status: string;
}

export interface ReminderClient {
  _id: string;
  firstName: string;
  lastName: string;
}

// Main Reminder interface
export interface Reminder {
  _id: string;
  organizationId: string;
  createdById: string;

  title: string;
  description?: string;

  remindAt: string;
  reminderType: ReminderType;

  recurrence?: ReminderRecurrence;

  taskId?: string | ReminderTask;
  projectId?: string | ReminderProject;
  clientId?: string | ReminderClient;

  status: ReminderStatus;
  snoozedUntil?: string;
  triggeredAt?: string;
  completedAt?: string;

  triggerCount: number;
  lastTriggeredAt?: string;
  nextTriggerAt?: string;

  createdAt: string;
  updatedAt: string;
}

// Reminder with populated relations
export interface ReminderWithRelations extends Omit<Reminder, 'taskId' | 'projectId' | 'clientId'> {
  taskId?: ReminderTask;
  projectId?: ReminderProject;
  clientId?: ReminderClient;
}

// Create reminder request
export interface CreateReminderRequest {
  title: string;
  description?: string;
  remindAt: string;
  reminderType?: ReminderType;
  recurrence?: ReminderRecurrence;
  taskId?: string;
  projectId?: string;
  clientId?: string;
}

// Update reminder request
export interface UpdateReminderRequest {
  title?: string;
  description?: string;
  remindAt?: string;
  reminderType?: ReminderType;
  recurrence?: ReminderRecurrence;
  taskId?: string | null;
  projectId?: string | null;
  clientId?: string | null;
}

// Reminder filters
export interface ReminderFilters {
  status?: ReminderStatus[];
  type?: ReminderType;
  upcoming?: boolean;
  sortBy?: 'remindAt' | 'createdAt' | 'title';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

// Pagination
export interface ReminderPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

// Reminders list response
export interface RemindersResponse {
  success: boolean;
  data: {
    reminders: ReminderWithRelations[];
    pagination: ReminderPagination;
  };
}

// Single reminder response
export interface ReminderResponse {
  success: boolean;
  data: {
    reminder: ReminderWithRelations;
  };
}

// Snooze response
export interface SnoozeResponse {
  success: boolean;
  data: {
    reminder: ReminderWithRelations;
    message: string;
  };
}

// Status options for UI
export const REMINDER_STATUS_OPTIONS: {
  value: ReminderStatus;
  label: string;
  color: string;
}[] = [
  { value: 'pending', label: 'Pending', color: '#1976d2' },
  { value: 'triggered', label: 'Triggered', color: '#ed6c02' },
  { value: 'snoozed', label: 'Snoozed', color: '#9c27b0' },
  { value: 'dismissed', label: 'Dismissed', color: '#757575' },
  { value: 'completed', label: 'Completed', color: '#2e7d32' },
];

// Snooze options
export const SNOOZE_OPTIONS = [
  { value: 5, label: '5 minutes' },
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 180, label: '3 hours' },
  { value: 1440, label: 'Tomorrow' },
];
